import { useSelector } from 'react-redux';
import { entries, keyBy } from 'lodash';
import { Tab, TabList, TabPanels, Tabs } from '@chakra-ui/react';
import NormalTable, { TNormalTable } from '../table/NormalTable';
import { TState, useActions } from '../../redux/store';
import { allIcons, NormalIcon } from '../utils/Icon';
import { useState } from 'react';
import { fetchPdfList, fetchStats } from '../../api/tabsApi';

export const defaultCustomData = [
  {
    name: 'none',
    value: 'default',
  },
  {
    name: 'main',
    value: 'default',
  },
  {
    name: 'mainBis',
    value: 'default',
  },
  {
    name: 'secondary',
    value: 'default',
  },
  {
    name: 'btnBg',
    value: 'default',
  },
  {
    name: 'lighter',
    value: 'default',
  },
  {
    name: 'light',
    value: 'default',
  },
  {
    name: 'normal',
    value: 'default',
  },
  {
    name: 'dark',
    value: 'default',
  },
  {
    name: 'darker',
    value: 'default',
  },
];

const usersColumns = [
  {
    header: <>Login</>,
    accessorKey: 'login',
    meta: { _id: 'login' },
  },
  {
    header: <>Nom</>,
    accessorKey: 'name',
    meta: { _id: 'name' },
  },
  {
    header: <>Roles</>,
    accessorKey: 'role',
    meta: { _id: 'role' },
  },
  {
    header: <>Désactivé</>,
    accessorKey: 'blocked',
    meta: { _id: 'blocked', type: 'switch' },
  },
  {
    header: <>Sans restriction</>,
    accessorKey: 'noRestrict',
    meta: { _id: 'noRestrict', type: 'switch' },
  },
  {
    header: <>MDP Temporaire</>,
    accessorKey: 'tempPassword',
    meta: { _id: 'tempPassword' },
  },
];

const rolesColumns = [
  {
    header: <>Nom</>,
    accessorKey: 'name',
    meta: { _id: 'name' },
  },
];

const inputsColumns = [
  {
    header: <>Nom</>,
    accessorKey: 'label',
    meta: { _id: 'label' },
  },
  {
    accessorKey: 'ID',
    accessorFn: (row: any) => row.name_id,
    meta: { _id: 'name_id' },
  },
  {
    accessorKey: 'Description',
    accessorFn: (row: any) => row.description,
    meta: { _id: 'description' },
  },
  {
    accessorKey: 'group',
    meta: { _id: 'group' },
  },
  {
    accessorKey: 'type',
    meta: { _id: 'type' },
  },
];

const documentColumns = [
  {
    header: <>Nom</>,
    accessorKey: 'name',
    meta: { _id: 'name' },
  },
  {
    header: <>Description</>,
    accessorKey: 'description',
    meta: { _id: 'description' },
  },
];

const customColumns = [
  {
    header: <>Nom</>,
    accessorKey: 'name',
    meta: { _id: 'name' },
  },
  {
    header: <>Valeur</>,
    accessorKey: 'value',
    meta: { _id: 'value', colorpicker: true },
  },
];

const actionsColumns = [
  {
    header: <>Nom</>,
    accessorKey: 'name',
    meta: { _id: 'name' },
  },
  // {
  //   header: <>Valeurs</>,
  //   id: 'value',
  //   meta: { _id: 'value' },
  //   accessorFn: (row: any) =>
  //     row.list
  //       .map((action: any) => `${action.name_id} => ${action.value}`)
  //       .join(' / '),
  // },
];

const pdfColumns = [
  {
    header: <>Nom</>,
    accessorKey: 'name',
    meta: { _id: 'name' },
  },
];

const statsColumns = [
  {
    header: <>Titre</>,
    accessorKey: 'title',
    meta: { _id: 'title' },
  },
];

const SettingsTabs = () => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const userList = useSelector((state: TState) => state.userList);
  const roleList = useSelector((state: TState) => state.roleList);
  const settingsTabs = useSelector((state: TState) => state.settingsTabs);
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const documentList = useSelector((state: TState) => state.documentList);
  const actionsList = useSelector((state: TState) => state.actionsList);
  const isSuper = useSelector((state: TState) => state.isSuper);
  const theme = useSelector((state: TState) => state.theme);

  const [asyncData, setAsyncData] = useState([]);

  const {
    setTabToUpdate,
    getUsersList,
    getRolesList,
    getInputs,
    getDocumentList,
    getActionsList,
    setModalData,
  } = useActions();

  const rolesById = keyBy(roleList, '_id');

  const tables: Record<string, TNormalTable> = {
    inputs: {
      data: dataInputs || [],
      columns: inputsColumns,
      editEntryKey: 'editInput',
      pendingKey: 'global/async/getInputs/pending',
      onLoad: async () => {
        getInputs();
        getRolesList();
      },
    },
    users: {
      data: [
        ...userList.map((u) => ({
          ...u,
          role: u.role_id.map((id: string) => rolesById[id]?.name).join(', '),
        })),
      ].reverse(),
      columns: isSuper
        ? [
            ...usersColumns,
            {
              header: <>Forfait</>,
              accessorKey: 'plan',
              meta: { _id: 'plan' },
            },
          ]
        : usersColumns,
      editEntryKey: 'editUser',
      onLoad: async () => {
        getRolesList();
        getUsersList();
      },
      pendingKey: 'global/async/getUsersList/pending',
    },
    roles: {
      data: roleList || [],
      columns: rolesColumns,
      editEntryKey: 'editRole',
      onLoad: getRolesList,
      pendingKey: 'global/async/getRolesList/pending',
    },
    documents: {
      data: documentList || [],
      columns: documentColumns,
      editEntryKey: 'editDocument',
      onLoad: getDocumentList,
      pendingKey: 'global/async/getDocumentList/pending',
    },
    custom: {
      data: entries(theme)
        .map(([k, v]) => ({ name: k, value: v }))
        .filter((row) => !/fontsize|margin/gi.test(row.name)),
      columns: customColumns,
      editEntryKey: 'editCustom',
      pendingKey: 'global/async/getUsersList/pending',
    },
    actions: {
      data: actionsList,
      columns: actionsColumns,
      editEntryKey: 'editQuickActions',
      pendingKey: 'global/async/getActionsList/pending',
      onLoad: async () => {
        getActionsList();
      },
    },
    pdf: {
      data: asyncData,
      columns: pdfColumns,
      editEntryKey: 'editPdfMatching',
      pendingKey: '',
      onLoad: async () => {
        const resp = await fetchPdfList();
        setAsyncData(resp.data);
      },
    },
    stats: {
      data: asyncData,
      columns: statsColumns,
      editEntryKey: 'editStats',
      pendingKey: '',
      formatData: ({ options, filtersOptions, ...data }: any) => ({
        ...data,
        ...options,
        ...filtersOptions,
      }),
      onLoad: async () => {
        const refreshData = async () => {
          const resp = await fetchStats();
          setAsyncData(resp.data);
        };

        refreshData();
        setModalData({ refreshData });
      },
    },
  };

  return (
    <Tabs display='flex' flexDirection='column' height='100%' overflow='hidden'>
      <TabList bgColor={theme.none} w='fit-content' borderRadius={5} px={1}>
        {settingsTabs.map((setting) => (
          <Tab
            key={setting._id}
            fontWeight='500'
            textTransform='capitalize'
            onClick={() => {
              setTabToUpdate(setting);
            }}
          >
            <NormalIcon as={allIcons?.[setting.icon || '']} boxSize={21} />
            {setting.title}
          </Tab>
        ))}
      </TabList>
      <TabPanels flexGrow={1} overflow='hidden' mt={3}>
        {tables[tabToUpdate?._id || ''] && (
          <NormalTable {...tables[tabToUpdate?._id as any]} />
        )}
      </TabPanels>
    </Tabs>
  );
};

export default SettingsTabs;
